import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Incident : famille "événement dommageable" (sinistre).
 *
 * Événement indésirable ou dommageable survenu (casse, blessure, perte, dégât),
 * documenté avec gravité, circonstances et responsabilités. Construite sur le
 * modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas d'incident concret ("griffure", "dégât des eaux",
 * "accrochage") : il connaît la famille générique `incident`. Le vocabulaire
 * métier est porté par le champ `type` (chaîne libre déclarée par l'app), la
 * gravité par `severity` (état métier libre : "mineur", "grave", "corporel").
 *
 * Règle métier (cf. bible) : on crée un incident seulement si le dommage engage
 * une responsabilité ou une déclaration (assurance, parents, autorité) ; l'usure
 * et la panne ordinaires restent `ticket` + `work-order`.
 *
 * Parties impliquées (`involvedRefs`) : liste structurée et hétérogène qui
 * référence les contacts, assets et lieux concernés (chaque entrée : nature, id,
 * rôle). C'est LE mécanisme de rattachement des familles `contact`, `asset` et
 * `place` : on n'ajoute donc pas de `contactId` / `assetId` séparés. Les autres
 * liens du dossier sont de simples ids (aucun import de schéma) : `custodyId`
 * (bien confié touché), `inspectionId` (constat qui révèle le dommage),
 * `ticketId` (réclamation liée), `followUpTaskIds` (actions correctives à mener,
 * plusieurs `task`), `documentIds` (photos, constat, déclaration).
 *
 * Sécurité / RGPD : la famille reste org-wide en v0 (même doctrine que asset).
 * Un incident corporel est une donnée de santé : l'accès fin (rôles direction et
 * workers cités au dossier, représentant légal pour un mineur) viendra après v0.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille incident (hors champs standard du socle). */
export const incidentCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "casse", "blessure", "degat_des_eaux"). */
  type: z.string().min(1),
  /** Date de survenue de l'événement. Optionnel. */
  occurredAt: z.date().optional(),
  /** Gravité, chaîne libre pilotée par l'app (ex. "mineur", "grave", "corporel"). Optionnel. */
  severity: z.string().min(1).optional(),
  /** Parties impliquées (contacts, assets, lieux) : nature, id, rôle. Liste structurée, optionnel. */
  involvedRefs: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Description des circonstances. Optionnel. */
  description: z.string().min(1).optional(),
  /** Actions immédiates documentées (intitulé, date, auteur...). Liste structurée, optionnel. */
  actionsTaken: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Date de clôture du dossier. Optionnel. */
  closedAt: z.date().optional(),
  // insuranceClaimRef : sensible (jamais org-wide), ira dans un sous-document privé (cf. WIP), hors v0.
  /** Bien confié touché (référence `custody`). Optionnel. */
  custodyId: z.string().min(1).optional(),
  /** Constat qui révèle le dommage (référence `inspection`). Optionnel. */
  inspectionId: z.string().min(1).optional(),
  /** Réclamation client liée (référence `ticket`). Optionnel. */
  ticketId: z.string().min(1).optional(),
  /** Actions correctives à mener (références `task`). Optionnel. */
  followUpTaskIds: z.array(z.string()).optional(),
  /** Pièces jointes : photos, constat, déclaration (références `document`). Optionnel. */
  documentIds: z.array(z.string()).optional(),
} as const;

/** Schéma complet de la famille incident (champs standard + champs métier). */
export const IncidentSchema = z.object({
  ...baseEntityFields,
  ...incidentCoreFields,
});

/** Type d'un incident. */
export type Incident = z.infer<typeof IncidentSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const IncidentCreateInputSchema = z.object({
  ...incidentCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un incident. */
export type IncidentCreateInput = z.infer<typeof IncidentCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const IncidentUpdateInputSchema = z
  .object({ ...incidentCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un incident. */
export type IncidentUpdateInput = z.infer<typeof IncidentUpdateInputSchema>;
